<template>
  <div class="relative" :class="brandClasses.container">
    <div>
      <base-link aria-hidden="true" class="shrink-0" tabindex="-1" :to="upsell.url" @click="handleClick()">
        <base-picture
          :alt="upsell.name"
          :height="image.height"
          :src="getImageTransformations(selection?.product.image || upsell.image, image)"
          :width="image.width"
        />
      </base-link>
    </div>
    <div class="f-col grow" :class="brandClasses.content">
      <span class="block self-start pr-10">
        <vf-link :class="brandClasses.name" :to="upsell.url" variant="quiet" @click="handleClick()">
          {{ upsell.name }}
        </vf-link>
      </span>
      <p v-if="selection" class="capitalize" :class="brandClasses.attributes">
        {{ [selection.attributeLabels?.color?.value, selection.attributeLabels?.size?.value]
          .filter(Boolean).join(' | ') }}
      </p>
      <div class="mt-a flex items-center gap-2 between">
        <product-pricing
          :class="brandClasses.price"
          :class-current="brandClasses.priceCurrent"
          :class-original="brandClasses.priceOriginal"
          :currency
          :price="upsell.price"
        />
        <vf-link
          v-if="selection"
          data-test-id="bundled-upsell-remove"
          size="sm"
          @click="emit('remove', upsell.id)"
        >
          {{ $t.remove }}
        </vf-link>
      </div>
    </div>
    <base-button
      v-if="selection"
      class="absolute right-1 top-1"
      data-test-id="bundled-upsell-edit"
      @click="emit('openQuickshop', { ...upsell, attributes: [], currency, variants: {} as ProductVariants })"
    >
      <span class="m-0.5 block p-1 duration">
        <vf-icon name="pencil" :size="editIconSize" />
        <span class="sr-only">{{ $t.editItem }}</span>
      </span>
    </base-button>
    <base-button
      v-else
      class="absolute right-1 top-1"
      data-test-id="bundled-upsell-add"
      @click="emit('openQuickshop', { ...upsell, attributes: [], currency, variants: {} as ProductVariants })"
    >
      <span class="m-0.5 block p-1 duration">
        <vf-icon name="plus" />
        <span class="sr-only">{{ $t.productUpsellAddToCart }}</span>
      </span>
    </base-button>
  </div>
</template>

<script lang="ts" setup>
import type { Product, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { ProductClickAction } from '#types/gtm'
import type { BundleSelectionItem, ProductUpsell } from '#types/product'

export type Props = {
  selection?: BundleSelectionItem
  upsell: ProductUpsell
}

export type Emits = {
  openQuickshop: [product: Product]
  recClick: [id: string, action?: ProductClickAction]
  remove: [id: string]
}

const { upsell } = defineProps<Props>()
const emit = defineEmits<Emits>()

const { brandClasses, editIconSize, image } = useAppConfig().components.product.bundledUpsell
const { $gtm, $locale, $t } = useNuxtApp()
const currency = useCurrencyCode($locale)

const handleClick = (action: ProductClickAction = 'Navigate To PDP') => {
  emit('recClick', upsell.id, action)
  $gtm.push('upsell.onCrossSellDetails', upsell.analyticsId || upsell.id)
}
</script>
